// The one place that still knows the old browser-stored cart. It exists only so shoppers who last visited
// before the server-stored cart shipped keep their items: read the old key once, send it to
// POST /controllers/cart/import, and remove the key only after the server answers 200/201.
// Plan IRO-3676 section 7. Delete this file (and its test allowlist entry) once the 60-day overlap ends.
//
// No Vue, axios or Mango imports: node tests load this file directly.

export const LEGACY_CART_KEY = 'cart'
export const LEGACY_CART_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

function storageOf(storage) {
	if (storage) return storage
	try {
		return typeof window !== 'undefined' ? window.localStorage : null
	} catch {
		return null
	}
}

export function removeLegacyCart(storage) {
	try {
		storageOf(storage)?.removeItem?.(LEGACY_CART_KEY)
	} catch {
		// storage can throw in private mode
	}
}

/** The parsed old cart, or null when absent, unreadable or older than the 30-day window it always had. */
export function readLegacyCart(storage, { now = Date.now() } = {}) {
	let raw
	try {
		raw = storageOf(storage)?.getItem?.(LEGACY_CART_KEY)
	} catch {
		return null
	}
	if (!raw) return null
	let cart
	try {
		cart = JSON.parse(raw)
	} catch {
		return null
	}
	if (!cart || typeof cart !== 'object') return null
	const updated = cart.updated ? new Date(cart.updated).getTime() : NaN
	if (Number.isFinite(updated) && now - updated > LEGACY_CART_MAX_AGE_MS) return null
	return cart
}

const hasContents = (cart) => !!(cart?.cartItems?.length || cart?.certificates?.length)

/**
 * Import a leftover browser cart. There is no rollout gate: a missing or `legacy` server setting must never
 * hide a shopper's items, so the new front always imports once; the key goes away only after the server
 * accepted the cart (and, below, the cart reads back).
 * A key that holds nothing worth keeping (garbage, stale, empty) is removed without a request.
 * A failed request keeps the key so the next page load tries again; so does an import the server accepted
 * but a follow-up read cannot see (`unverified`, e.g. the guest cookie was refused).
 * @returns {{ status: string, cart?: object, warnings?: any[] }}
 */
export async function importLegacyCartIfPresent({ cartApi, storage } = {}) {
	const store = storageOf(storage)
	let present
	try {
		present = store?.getItem?.(LEGACY_CART_KEY)
	} catch {
		return { status: 'none' }
	}
	if (!present) return { status: 'none' }
	const legacy = readLegacyCart(store)
	if (!legacy || !hasContents(legacy)) {
		removeLegacyCart(store)
		return { status: 'discarded' }
	}
	const result = await cartApi.importLegacy(legacy)
	if (!result.ok) return { status: 'failed', error: result.error, code: result.code }
	// A guest import hands the browser a cookie. If it did not stick, the server cart is unreachable and the
	// old key is the only copy left, so it stays until a follow-up read returns the imported lines.
	if (hasContents(result.cart) && typeof cartApi.get === 'function') {
		const check = await cartApi.get()
		if (!check.ok || !hasContents(check.cart)) return { status: 'unverified', cart: result.cart, warnings: result.warnings || [], code: check.code }
	}
	removeLegacyCart(store)
	return { status: result.imported === false ? 'already_imported' : 'imported', cart: result.cart, warnings: result.warnings || [] }
}
